import * as Tabs from '@radix-ui/react-tabs';
import { useState } from 'react';
import { createRoot } from 'react-dom/client';

import styles from './style.module.scss';

type TabData = {
  id: string;
  labelHtml: string;
  panelHtml: string;
};

const selectTabsBlocks = () =>
  Array.from(
    document.querySelectorAll<HTMLElement>(`[data-tabs-root="true"][data-hydrated="false"]`),
  );

const extractTabs = (root: HTMLElement): TabData[] => {
  const items = Array.from(root.querySelectorAll<HTMLElement>('[data-tab-item]'));
  return items
    .map((item) => {
      const id = item.dataset.tabId || '';
      const label = item.querySelector<HTMLElement>('[data-tab-label]');
      const panel = item.querySelector<HTMLElement>('[data-tab-panel]');
      return {
        id,
        labelHtml: label?.innerHTML ?? '',
        panelHtml: panel?.innerHTML ?? '',
      };
    })
    .filter((item) => item.id);
};

const TabsShell = ({
  tabs,
  defaultValue,
  orientation,
}: {
  tabs: TabData[];
  defaultValue: string;
  orientation: 'horizontal' | 'vertical';
}) => {
  const [value, setValue] = useState(defaultValue);

  const handleChange = (nextValue: string) => {
    setValue(nextValue);

    const tabIndex = tabs.findIndex((t) => t.id === nextValue);
    const tab = tabs[tabIndex];
    if (tab) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = tab.labelHtml;
      const label = (tempDiv.textContent || '').trim();

      window.InteractTracking?.track('feature_tab_interaction', {
        tab_label: label,
        tab_index: tabIndex,
        page_name: document.title,
      });
    }
  };

  return (
    <Tabs.Root
      value={value}
      onValueChange={handleChange}
      orientation={orientation}
      className={styles.tabsRoot}
    >
      <div className={styles.content}>
        <div className={styles.listWrapper}>
          <Tabs.List className={styles.list}>
            {tabs.map((tab) => (
              <Tabs.Trigger
                key={tab.id}
                value={tab.id}
                className={styles.trigger}
                dangerouslySetInnerHTML={{ __html: tab.labelHtml }}
              />
            ))}
          </Tabs.List>
        </div>
        <div className={styles.panels}>
          {tabs.map((tab) => (
            <Tabs.Content key={tab.id} value={tab.id} className={styles.panel}>
              <div dangerouslySetInnerHTML={{ __html: tab.panelHtml }} />
            </Tabs.Content>
          ))}
        </div>
      </div>
    </Tabs.Root>
  );
};

const hydrateTabs = (root: HTMLElement) => {
  const tabs = extractTabs(root);
  if (!tabs.length) return;

  const defaultValue =
    root.dataset.defaultTabId && tabs.some((tab) => tab.id === root.dataset.defaultTabId)
      ? root.dataset.defaultTabId
      : tabs[0].id;

  const orientation = root.dataset.tabsOrientation === 'vertical' ? 'vertical' : 'horizontal';

  root.innerHTML = '';

  const mount = createRoot(root);
  mount.render(<TabsShell tabs={tabs} defaultValue={defaultValue} orientation={orientation} />);

  root.dataset.hydrated = 'true';
};

const start = () => {
  const hydrate = () => {
    selectTabsBlocks().forEach(hydrateTabs);
  };

  const observer = new MutationObserver(hydrate);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  hydrate();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
